import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import Modal from 'components/ui/Modal';
import * as CollectionsModule from 'modules/collections';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, the modal edits this collection; otherwise it creates one. */
  collection?: { id: string; name: string; description?: string } | null;
  onSuccess: () => void;
}

export const CollectionFormModal: React.FC<Props> = ({ open, onClose, collection, onSuccess }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createCollection = CollectionsModule.Hooks.useCreate();
  const updateCollection = CollectionsModule.Hooks.useUpdate();
  const isEditing = Boolean(collection);
  const isPending = createCollection.isPending || updateCollection.isPending;

  // Seed fields from the collection when editing (and reset on open/close).
  useEffect(() => {
    setName(collection?.name ?? '');
    setDescription(collection?.description ?? '');
  }, [collection, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      if (collection) {
        await updateCollection.mutateAsync({
          id: collection.id,
          name: name.trim(),
          description: description.trim() || undefined,
        });
      } else {
        await createCollection.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
        });
      }
      onSuccess();
    } catch {
      // handled by hook toast
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={isEditing ? t('collections.edit_collection') : t('collections.create_collection')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="collection-name">{t('collections.name')}</Label>
          <Input
            id="collection-name"
            placeholder={t('collections.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="collection-description">{t('collections.description_optional')}</Label>
          <Textarea
            id="collection-description"
            placeholder={t('collections.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={!name.trim() || isPending}>
            {isPending ? t('collections.saving') : isEditing ? t('collections.save_changes') : t('collections.create_collection')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CollectionFormModal;
